import {
  createPdfDetailRows,
  createPdfResult,
  createPdfSection,
  getPdfRowName,
  getSignedPdfAdjustmentAmount,
  hasPdfSectionActivity,
  sumPdfRows,
} from "../shared";
import { INVEST_CO_MAIN_COMP_LINE_BY_ID } from "./lineConfig";

const getSignedRoundedAdjustmentAmount = (row, amountKey = "amount") =>
  getSignedPdfAdjustmentAmount(row, amountKey, {
    preserveCaSummarySign: true,
    round: true,
  });

const createSection = (id, rows, total, overrides = {}) =>
  createPdfSection(INVEST_CO_MAIN_COMP_LINE_BY_ID, id, rows, total, overrides);

const createResult = (id, amount, overrides = {}) =>
  createPdfResult(INVEST_CO_MAIN_COMP_LINE_BY_ID, id, amount, overrides);

const getInvestmentIncomeRows = (referenceData = {}) => [
  ...(referenceData.taxAdjustments || []),
  ...(referenceData.taxAdjustmentSeparateSourcedIncome || []),
  ...(referenceData.taxAdjustmentBalancingCharge || []),
];

export const buildInvestCoMainCompSections = ({
  amountKey = "amount",
  referenceData = {},
}) => {
  const sections = [];
  const investmentIncomeRows = getInvestmentIncomeRows(referenceData);
  const investmentIncomeDetailRows = createPdfDetailRows(
    investmentIncomeRows,
    getSignedRoundedAdjustmentAmount,
    getPdfRowName,
    amountKey,
  );
  const investmentIncomeAdjustmentTotal = sumPdfRows(
    investmentIncomeRows,
    getSignedRoundedAdjustmentAmount,
    amountKey,
  );
  const totalInvestmentIncome = investmentIncomeAdjustmentTotal;
  const currentYearCaRows = referenceData.currentYearCapitalAllowances || [];
  const currentYearCaDetailRows = createPdfDetailRows(
    currentYearCaRows,
    getSignedRoundedAdjustmentAmount,
    getPdfRowName,
    amountKey,
  );
  const currentYearCaTotal = sumPdfRows(
    currentYearCaRows,
    getSignedRoundedAdjustmentAmount,
    amountKey,
  );
  const hasCurrentYearCa = hasPdfSectionActivity(
    currentYearCaDetailRows,
    currentYearCaTotal,
  );
  const currentYearDonationRows = referenceData.currentYearDonations || [];
  const currentYearDonationDetailRows = createPdfDetailRows(
    currentYearDonationRows,
    getSignedRoundedAdjustmentAmount,
    getPdfRowName,
    amountKey,
  );
  const currentYearDonationTotal = sumPdfRows(
    currentYearDonationRows,
    getSignedRoundedAdjustmentAmount,
    amountKey,
  );
  const hasCurrentYearDonations = hasPdfSectionActivity(
    currentYearDonationDetailRows,
    currentYearDonationTotal,
  );
  const currentYearLossRows = referenceData.currentYearInvestmentLosses || [];
  const currentYearLossDetailRows = createPdfDetailRows(
    currentYearLossRows,
    getSignedRoundedAdjustmentAmount,
    getPdfRowName,
    amountKey,
  );
  const currentYearLossTotal = sumPdfRows(
    currentYearLossRows,
    getSignedRoundedAdjustmentAmount,
    amountKey,
  );
  const hasCurrentYearLosses = hasPdfSectionActivity(
    currentYearLossDetailRows,
    currentYearLossTotal,
  );

  if (
    hasPdfSectionActivity(
      investmentIncomeDetailRows,
      investmentIncomeAdjustmentTotal,
    )
  ) {
    sections.push(
      createSection(
        "net_investment_income",
        investmentIncomeDetailRows,
        investmentIncomeAdjustmentTotal,
        {
          forceMultiRow: true,
          omitSubtotalRow: true,
          suppressSubtotalLine: true,
        },
      ),
    );
  }

  sections.push(
    createResult("total_investment_income", totalInvestmentIncome, {
      alwaysRender: true,
      singleLine:
        !hasCurrentYearCa && !hasCurrentYearDonations && !hasCurrentYearLosses,
      suppressTopLine: true,
    }),
  );

  if (hasCurrentYearCa) {
    sections.push(
      createSection("current_year_ca", currentYearCaDetailRows, currentYearCaTotal),
    );
  }

  if (hasCurrentYearLosses) {
    sections.push(
      createSection(
        "current_year_losses",
        currentYearLossDetailRows,
        currentYearLossTotal,
      ),
    );
  }

  if (hasCurrentYearDonations) {
    sections.push(
      createSection(
        "current_year_donations",
        currentYearDonationDetailRows,
        currentYearDonationTotal,
      ),
    );
  }

  const chargeableIncomeBeforeExemptAmount =
    totalInvestmentIncome +
    currentYearCaTotal +
    currentYearDonationTotal +
    currentYearLossTotal;

  sections.push(
    createResult(
      "chargeable_income_before_exempt_amount",
      chargeableIncomeBeforeExemptAmount,
      {
        alwaysRender: true,
        doubleLine: chargeableIncomeBeforeExemptAmount <= 0,
      },
    ),
  );

  return sections;
};

export default buildInvestCoMainCompSections;
